import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { t } from '../../i18n';
import { showToast } from '../../components/ui/toast';
import { icon } from '../../components/ui/icons';

interface CalendarInfo { enabled: boolean; url: string; token: string; }

/** Student portal — "Subscribe to calendar": shows the private iCal feed URL with a
 *  copy button and instructions for Google / Apple / Outlook. */
@customElement('cadence-portal-calendar')
export class CadencePortalCalendar extends LitElement {
  @state() private info: CalendarInfo | null = null;
  @state() private loading = true;

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 var(--space-2); font-size: 1.6rem; }
    .lede { color: var(--color-text-secondary); margin: 0 0 var(--space-4); max-width: 60ch; }
    .stack { display: grid; gap: var(--space-4); }
    .url-row { display: flex; gap: 8px; align-items: stretch; flex-wrap: wrap; }
    .url {
      flex: 1; min-width: 240px; font-family: var(--font-mono, monospace); font-size: 0.85rem;
      padding: 10px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md);
      background: var(--color-bg-secondary); color: var(--color-text-primary);
      overflow-x: auto; white-space: nowrap;
    }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    h2 { font-size: 1.05rem; margin: 0 0 6px; }
    ol { margin: 0; padding-left: 1.3em; line-height: 1.7; color: var(--color-text-secondary); }
    ol li code { background: var(--color-bg-secondary); padding: 1px 5px; border-radius: 4px; font-size: 0.85em; }
    .apps { display: grid; gap: var(--space-3); grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .note { color: var(--color-text-secondary); font-size: 0.85rem; }
    a.btnlink { text-decoration: none; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try { this.info = await api.get<CalendarInfo>('/portal/calendar'); }
    catch { this.info = null; }
    finally { this.loading = false; }
  }

  private async copy() {
    if (!this.info) return;
    try {
      await navigator.clipboard.writeText(this.info.url);
      showToast(t('common.calendar.copied'), 'success');
    } catch {
      showToast(t('common.calendar.copyFailed'), 'error');
    }
  }

  private get webcalUrl(): string {
    return this.info ? this.info.url.replace(/^https?:\/\//, 'webcal://') : '';
  }

  private get googleUrl(): string {
    return `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(this.webcalUrl)}`;
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    if (!this.info || !this.info.enabled) {
      return html`
        <h1>${t('common.calendar.title')}</h1>
        <cadence-empty-state heading=${t('common.calendar.disabledHeading')} message=${t('common.calendar.disabledMessage')}></cadence-empty-state>
      `;
    }
    return html`
      <h1>${t('common.calendar.title')}</h1>
      <p class="lede">${t('common.calendar.lede')}</p>
      <div class="stack">
        <cadence-card>
          <h2>${t('common.calendar.feedUrl')}</h2>
          <div class="url-row">
            <div class="url" data-testid="calendar-url">${this.info.url}</div>
            <div class="actions">
              <cadence-button @click=${() => this.copy()}>${icon('copy', 16)} ${t('common.calendar.copy')}</cadence-button>
              <a class="btnlink" href=${this.webcalUrl}><cadence-button variant="secondary">${t('common.calendar.subscribe')}</cadence-button></a>
            </div>
          </div>
          <p class="note">${t('common.calendar.readOnlyNote')}</p>
        </cadence-card>

        <div class="apps">
          <cadence-card>
            <h2>${t('common.calendar.google')}</h2>
            <ol>
              <li>${t('common.calendar.googleStep1')}</li>
              <li>${t('common.calendar.googleStep2')}</li>
              <li>${t('common.calendar.googleStep3')}</li>
            </ol>
            <p><a class="btnlink" href=${this.googleUrl} target="_blank" rel="noopener"><cadence-button variant="ghost">${t('common.calendar.openGoogle')}</cadence-button></a></p>
          </cadence-card>
          <cadence-card>
            <h2>${t('common.calendar.apple')}</h2>
            <ol>
              <li>${t('common.calendar.appleStep1')}</li>
              <li>${t('common.calendar.appleStep2')}</li>
              <li>${t('common.calendar.appleStep3')}</li>
            </ol>
          </cadence-card>
          <cadence-card>
            <h2>${t('common.calendar.outlook')}</h2>
            <ol>
              <li>${t('common.calendar.outlookStep1')}</li>
              <li>${t('common.calendar.outlookStep2')}</li>
              <li>${t('common.calendar.outlookStep3')}</li>
            </ol>
          </cadence-card>
        </div>
      </div>
    `;
  }
}
